import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronRight,
  FiExternalLink,
  FiFileText,
  FiHelpCircle,
  FiRefreshCw,
  FiSearch,
  FiShield,
} from "react-icons/fi";
import { acknowledgePolicy, getPolicies, getPolicy } from "../api/hrmApi";
import { normalizePolicies, normalizePolicyResponse } from "../api/hrmNormalizers";
import ComponentLoader from "../components/ComponentLoader";
import ScreenHeader from "./ScreenHeader";

const statusFilters = ["All", "To acknowledge", "Acknowledged", "Published"];
const EMPTY_POLICIES = [];

const getPolicyTone = (policy = {}) => {
  if (policy.acknowledged) {
    return "green";
  }
  if (policy.requiresAcknowledgement) {
    return "amber";
  }
  return "blue";
};

const formatAckDate = (date = new Date()) =>
  date.toLocaleString("en-IN", {
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    month: "short",
    year: "numeric",
  });

const markPolicyAcknowledged = (policy = {}, acknowledgedAt = "") => ({
  ...policy,
  acknowledged: true,
  acknowledgedAt: acknowledgedAt || policy.acknowledgedAt || "Completed",
  status: "Acknowledged",
  tone: "green",
});

const matchesStatusFilter = (policy, filter) => {
  if (filter === "All") {
    return true;
  }
  if (filter === "To acknowledge") {
    return policy.requiresAcknowledgement && !policy.acknowledged;
  }
  if (filter === "Acknowledged") {
    return policy.acknowledged;
  }
  return !policy.requiresAcknowledgement || String(policy.status || "").toLowerCase().includes("published");
};

const PoliciesScreen = ({ onAction, onPoliciesChange, policies = EMPTY_POLICIES, viewUserId = "" }) => {
  const [policyList, setPolicyList] = useState(policies);
  const [selectedPolicyId, setSelectedPolicyId] = useState("");
  const [expandedPolicyId, setExpandedPolicyId] = useState("");
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");
  const [statusFilter, setStatusFilter] = useState("All");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [acknowledging, setAcknowledging] = useState(false);
  const acknowledgedPolicyIdsRef = useRef(new Set());
  const policyDetailRequestRef = useRef(0);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");

  useEffect(() => {
    setPolicyList(policies);
  }, [policies]);

  const updatePolicies = useCallback((nextPolicies) => {
    setPolicyList((currentPolicies) =>
      typeof nextPolicies === "function" ? nextPolicies(currentPolicies) : nextPolicies
    );
  }, []);

  const applyLocalAcknowledgements = useCallback(
    (nextPolicies) =>
      nextPolicies.map((policy) =>
        acknowledgedPolicyIdsRef.current.has(String(policy.id)) ? markPolicyAcknowledged(policy) : policy
      ),
    []
  );

  useEffect(() => {
    onPoliciesChange?.(policyList);
  }, [onPoliciesChange, policyList]);

  const loadPolicyDetail = useCallback(async (policy) => {
    if (!policy?.id) {
      policyDetailRequestRef.current += 1;
      setSelectedPolicy(null);
      return;
    }

    const requestId = ++policyDetailRequestRef.current;
    setSelectedPolicy(policy);
    setDetailLoading(true);
    try {
      const payload = await getPolicy(viewUserId, policy.id);
      if (requestId !== policyDetailRequestRef.current) {
        return;
      }
      const normalizedPolicy = normalizePolicyResponse(payload, policy);
      const nextPolicy = acknowledgedPolicyIdsRef.current.has(String(normalizedPolicy.id))
        ? markPolicyAcknowledged(normalizedPolicy)
        : normalizedPolicy;
      setSelectedPolicy(nextPolicy);
      updatePolicies((currentPolicies) =>
        currentPolicies.map((currentPolicy) =>
          currentPolicy.id === nextPolicy.id ? nextPolicy : currentPolicy
        )
      );
    } catch (loadError) {
      if (requestId === policyDetailRequestRef.current) {
        setError(loadError.message || "Policy detail could not be loaded.");
      }
    } finally {
      if (requestId === policyDetailRequestRef.current) {
        setDetailLoading(false);
      }
    }
  }, [updatePolicies, viewUserId]);

  const loadPolicies = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await getPolicies(viewUserId);
      const normalizedPolicies = applyLocalAcknowledgements(normalizePolicies(payload));
      updatePolicies(normalizedPolicies);
      const nextSelectedPolicy = normalizedPolicies[0] || null;
      setSelectedPolicy(nextSelectedPolicy);
      setSelectedPolicyId((currentId) =>
        normalizedPolicies.some((policy) => policy.id === currentId) ? currentId : normalizedPolicies[0]?.id || ""
      );
      if (nextSelectedPolicy) {
        loadPolicyDetail(nextSelectedPolicy);
      }
    } catch (loadError) {
      updatePolicies([]);
      setSelectedPolicy(null);
      setSelectedPolicyId("");
      setError(loadError.message || "Policies could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, [applyLocalAcknowledgements, loadPolicyDetail, updatePolicies, viewUserId]);

  useEffect(() => {
    loadPolicies();
  }, [loadPolicies]);

  useEffect(() => {
    if (!selectedPolicyId && policyList.length) {
      setSelectedPolicyId(policyList[0].id);
      setSelectedPolicy(policyList[0]);
    }
  }, [policyList, selectedPolicyId]);

  const categories = useMemo(
    () => ["All", "Required", ...Array.from(new Set(policyList.map((policy) => policy.category).filter(Boolean))).sort()],
    [policyList]
  );

  const filteredPolicies = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    return policyList.filter((policy) => {
      const matchesSearch = !query ||
        [policy.title, policy.category, policy.owner, policy.policyNumber]
          .join(" ")
          .toLowerCase()
          .includes(query);
      const matchesCategory = categoryFilter === "All" ||
        (categoryFilter === "Required" && policy.requiresAcknowledgement && !policy.acknowledged) ||
        policy.category === categoryFilter;
      return matchesSearch && matchesCategory && matchesStatusFilter(policy, statusFilter);
    });
  }, [categoryFilter, policyList, searchTerm, statusFilter]);

  useEffect(() => {
    if (expandedPolicyId && !filteredPolicies.some((policy) => policy.id === expandedPolicyId)) {
      setExpandedPolicyId("");
    }
  }, [expandedPolicyId, filteredPolicies]);

  const acknowledgementDue = useMemo(
    () => policyList.filter((policy) => policy.requiresAcknowledgement && !policy.acknowledged).length,
    [policyList]
  );
  const acknowledgedCount = useMemo(
    () => policyList.filter((policy) => policy.acknowledged).length,
    [policyList]
  );
  const priorityPolicy = useMemo(
    () => policyList.find((policy) => policy.requiresAcknowledgement && !policy.acknowledged) || null,
    [policyList]
  );
  const documentHref = selectedPolicy?.downloadUrl || selectedPolicy?.documentUrl || "";

  const getCategoryCount = (category) => {
    if (category === "All") {
      return policyList.length;
    }
    if (category === "Required") {
      return acknowledgementDue;
    }
    return policyList.filter((policy) => policy.category === category).length;
  };

  const handlePolicySelect = (policy) => {
    setMessage("");
    setError("");
    if (expandedPolicyId === policy.id) {
      setExpandedPolicyId("");
      return;
    }
    setExpandedPolicyId(policy.id);
    setSelectedPolicyId(policy.id);
    loadPolicyDetail(policy);
  };

  const openPolicyDetail = (policy) => {
    setMessage("");
    setError("");
    setExpandedPolicyId(policy.id);
    setSelectedPolicyId(policy.id);
    loadPolicyDetail(policy);
  };

  const handleAcknowledge = async () => {
    if (!selectedPolicy?.id) {
      return;
    }

    setAcknowledging(true);
    setError("");
    setMessage("");
    const acknowledgedAt = new Date();
    try {
      const payload = await acknowledgePolicy(viewUserId, selectedPolicy.id, {
        policyId: selectedPolicy.id,
        acknowledgedAt: acknowledgedAt.toISOString(),
        acknowledgementSource: "AWC HRM Portal",
        statement: "I have read and understood this policy.",
      });
      const fallbackPolicy = markPolicyAcknowledged(selectedPolicy, formatAckDate(acknowledgedAt));
      const acknowledgedPolicy = markPolicyAcknowledged(
        normalizePolicyResponse(payload, fallbackPolicy),
        fallbackPolicy.acknowledgedAt
      );
      acknowledgedPolicyIdsRef.current.add(String(acknowledgedPolicy.id));
      setSelectedPolicy(acknowledgedPolicy);
      updatePolicies((currentPolicies) =>
        currentPolicies.map((policy) => (policy.id === acknowledgedPolicy.id ? acknowledgedPolicy : policy))
      );
      setMessage("Acknowledgement saved.");
    } catch (ackError) {
      setError(ackError.message || "Acknowledgement could not be saved.");
    } finally {
      setAcknowledging(false);
    }
  };

  const renderSelectedPolicy = () => {
    if (!selectedPolicy) {
      return null;
    }

    return (
      <section
        className="new-hrm-policy-detail new-hrm-policy-inline-detail new-hrm-loader-host"
        id={`policy-detail-${selectedPolicy.id}`}
        aria-label={`${selectedPolicy.title} details`}
      >
        <ComponentLoader active={detailLoading || acknowledging} label={acknowledging ? "Saving acknowledgement" : "Loading policy"} />
        <div className="new-hrm-policy-detail__heading">
          <span className={`new-hrm-policy-detail__mark new-hrm-policy-status--${getPolicyTone(selectedPolicy)}`}>
            <FiShield aria-hidden="true" />
          </span>
          <div>
            <p>{selectedPolicy.category}</p>
            <h2>{selectedPolicy.title}</h2>
            <span>
              {selectedPolicy.policyNumber || "Policy"}{selectedPolicy.version ? ` - Version ${selectedPolicy.version}` : ""}
            </span>
          </div>
          <em className={`new-hrm-policy-status new-hrm-policy-status--${getPolicyTone(selectedPolicy)}`}>
            {selectedPolicy.status}
          </em>
        </div>

        <div className="new-hrm-policy-detail__meta">
          <span><small>Owner</small><strong>{selectedPolicy.owner || "HR"}</strong></span>
          <span><small>Effective</small><strong>{selectedPolicy.effectiveDate || "Not set"}</strong></span>
          <span><small>Updated</small><strong>{selectedPolicy.updated || "Not set"}</strong></span>
          <span><small>Acknowledged</small><strong>{selectedPolicy.acknowledgedAt || (selectedPolicy.acknowledged ? "Completed" : "Pending")}</strong></span>
        </div>

        <div className="new-hrm-policy-detail__body">
          <h3>Summary</h3>
          <p>{selectedPolicy.summary || "No summary has been added for this policy."}</p>
          <h3>Policy Detail</h3>
          <p>{selectedPolicy.body || "Open the attached document for the full policy details."}</p>
        </div>

        <div className="new-hrm-policy-detail__actions">
          {documentHref ? (
            <a className="new-hrm-secondary-button" href={documentHref} target="_blank" rel="noreferrer">
              <FiExternalLink /> Open Document
            </a>
          ) : (
            <span>Document link not provided.</span>
          )}
          {selectedPolicy.requiresAcknowledgement && !selectedPolicy.acknowledged ? (
            <button className="new-hrm-primary-button" type="button" onClick={handleAcknowledge} disabled={acknowledging}>
              <FiCheckCircle /> Acknowledge Policy
            </button>
          ) : (
            <strong>
              <FiCheckCircle /> Acknowledgement complete
            </strong>
          )}
        </div>
      </section>
    );
  };

  return (
    <section className="new-hrm-screen new-hrm-policies-screen new-hrm-policies-screen--reference">
      <ScreenHeader
        eyebrow="Governance"
        title="Policies"
        description="Find, read, and acknowledge the current rules for how we work."
        actions={
          <>
            <button className="new-hrm-icon-button" type="button" onClick={loadPolicies} aria-label="Refresh policies" title="Refresh policies">
              <FiRefreshCw aria-hidden="true" />
            </button>
            <button className="new-hrm-secondary-button" type="button" onClick={() => onAction("hr")}>
              <FiHelpCircle aria-hidden="true" />
              <span>Ask HR</span>
            </button>
          </>
        }
      />

      {priorityPolicy && (
        <section className="new-hrm-policy-priority" aria-labelledby="priority-policy-title">
          <span className="new-hrm-policy-priority__icon"><FiAlertTriangle aria-hidden="true" /></span>
          <div>
            <span className="new-hrm-reference-kicker">Action needed</span>
            <h2 id="priority-policy-title">{priorityPolicy.title}</h2>
            <p>{priorityPolicy.summary || "Review this required policy and record your acknowledgement."}</p>
          </div>
          <button className="new-hrm-primary-button" type="button" onClick={() => openPolicyDetail(priorityPolicy)}>
            <span>Review now</span>
            <FiChevronRight aria-hidden="true" />
          </button>
        </section>
      )}

      <section className="new-hrm-panel new-hrm-policy-workspace new-hrm-loader-host">
        <ComponentLoader active={loading} label="Loading policies" />
        <div className="new-hrm-policy-command">
          <div className="new-hrm-policy-command__summary">
            <span className={acknowledgementDue ? "new-hrm-policy-command__status new-hrm-policy-command__status--due" : "new-hrm-policy-command__status"}>
              <FiShield aria-hidden="true" />
              {acknowledgementDue ? `${acknowledgementDue} acknowledgement due` : "All required policies acknowledged"}
            </span>
            <div>
              <strong>{policyList.length}</strong>
              <small>available</small>
            </div>
            <div>
              <strong>{acknowledgedCount}</strong>
              <small>acknowledged</small>
            </div>
          </div>
          <div className="new-hrm-policy-toolbar">
            <label className="new-hrm-policy-search">
              <FiSearch aria-hidden="true" />
              <input
                type="search"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                placeholder="Search policies"
              />
            </label>
            <label>
              <span>Status</span>
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                {statusFilters.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {error && (
          <p className="new-hrm-empty-state new-hrm-policy-alert">
            <FiAlertTriangle /> {error}
          </p>
        )}
        {message && (
          <p className="new-hrm-empty-state new-hrm-policy-alert new-hrm-policy-alert--success">
            <FiCheckCircle /> {message}
          </p>
        )}

        <div className="new-hrm-policy-reference-layout">
          <nav className="new-hrm-policy-categories" aria-label="Policy categories">
            <strong>Browse</strong>
            {categories.map((category) => (
              <button
                className={categoryFilter === category ? "new-hrm-policy-category new-hrm-policy-category--active" : "new-hrm-policy-category"}
                key={category}
                type="button"
                aria-pressed={categoryFilter === category}
                onClick={() => setCategoryFilter(category)}
              >
                <span>{category}</span>
                <small>{getCategoryCount(category)}</small>
              </button>
            ))}
          </nav>

          <div className="new-hrm-policy-library">
            <header className="new-hrm-panel__header new-hrm-panel__header--compact new-hrm-policy-library-heading">
              <div>
                <h2>{categoryFilter === "All" ? "Policy library" : `${categoryFilter} policies`}</h2>
                <p>{filteredPolicies.length} current {filteredPolicies.length === 1 ? "policy" : "policies"} shown</p>
              </div>
            </header>
            {!filteredPolicies.length && !loading && (
              <p className="new-hrm-empty-state">No policies match the selected filters.</p>
            )}
            <div className="new-hrm-policy-live-rows">
              {filteredPolicies.map((policy) => {
                const selected = expandedPolicyId === policy.id;
                return (
                  <React.Fragment key={policy.id}>
                    <button
                      className={`new-hrm-policy-live-row${selected ? " new-hrm-policy-live-row--active" : ""}`}
                      type="button"
                      aria-controls={selected ? `policy-detail-${policy.id}` : undefined}
                      aria-expanded={selected}
                      onClick={() => handlePolicySelect(policy)}
                    >
                      <span className={`new-hrm-policy-live-row__icon new-hrm-policy-status--${getPolicyTone(policy)}`}>
                        <FiFileText aria-hidden="true" />
                      </span>
                      <span className="new-hrm-policy-live-row__copy">
                        <small>{policy.category} - {policy.owner || "HR"}</small>
                        <strong>{policy.title}</strong>
                        <p>{policy.summary || "Open the current policy to review the full details."}</p>
                      </span>
                      <span className="new-hrm-policy-live-row__meta">
                        <em className={`new-hrm-policy-status new-hrm-policy-status--${getPolicyTone(policy)}`}>
                          {policy.status}
                        </em>
                        <small>{policy.updated ? `Updated ${policy.updated}` : "Current policy"}</small>
                      </span>
                      <FiChevronRight className="new-hrm-policy-live-row__chevron" aria-hidden="true" />
                    </button>
                    {selected && renderSelectedPolicy()}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default PoliciesScreen;
